import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Breadcrumb from '../../../../components/Breadcrumb';
import Page from '../../../../components/Page';
import PrimaryButton from '../../../../components/PrimaryButton';
import client from '../../../../lib/feathers';

// ─── Constants ────────────────────────────────────────────────────────────────

const AUDIENCE_OPTIONS = [
  { value: 'outreach', label: 'Outreach' },
  { value: 'business', label: 'Business' },
  { value: 'personal', label: 'Personal' },
];

const TRIGGER_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'contact_created', label: 'Contact Created' },
  { value: 'listing_claimed', label: 'Listing Claimed' },
  { value: 'user_created', label: 'User Created' },
];

const LISTING_STATUS_OPTIONS = [
  { value: 'unverified', label: 'Unverified' },
  { value: 'verified', label: 'Verified' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'featured', label: 'Featured' },
];

const TEMPLATE_VARS = {
  outreach: [
    '{{contactName}}',
    '{{wineryName}}',
    '{{region}}',
    '{{wineCount}}',
    '{{checkinCount}}',
    '{{listCount}}',
    '{{listingUrl}}',
    '{{claimUrl}}',
  ],
  business: [
    '{{contactName}}',
    '{{wineryName}}',
    '{{region}}',
    '{{wineCount}}',
    '{{checkinCount}}',
    '{{listCount}}',
    '{{listingUrl}}',
    '{{claimUrl}}',
  ],
  personal: ['{{userName}}', '{{userEmail}}', '{{appUrl}}'],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const generateSlug = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const EMPTY_STEP = () => ({
  _key: Math.random().toString(36).slice(2),
  name: '',
  delayDays: 0,
  subject: '',
  body: '',
});

const EMPTY_FORM = {
  name: '',
  slug: '',
  description: '',
  audience: 'outreach',
  trigger: 'manual',
  masterTemplateId: '',
  fromEmail: '',
  fromName: '',
};

const EMPTY_TRIGGER_FILTER = {
  listingStatus: [],
};

// ─── Step card component ──────────────────────────────────────────────────────

function StepCard({ step, index, audience, onChange, onRemove, totalSteps }) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const vars = TEMPLATE_VARS[audience] || TEMPLATE_VARS.outreach;

  const handleField = (field, value) => {
    onChange({ ...step, [field]: value });
  };

  const handleRemoveClick = () => {
    if (confirmRemove) {
      onRemove();
    } else {
      setConfirmRemove(true);
    }
  };

  const handleRemoveBlur = () => {
    setConfirmRemove(false);
  };

  const inputClass =
    'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
      {/* Step header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-900">
            Step {index + 1}
          </span>
          {step.delayDays > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
              Day +{step.delayDays}
            </span>
          )}
          {step.delayDays === 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
              Immediate
            </span>
          )}
        </div>
        {totalSteps > 1 && (
          <button
            type="button"
            onBlur={handleRemoveBlur}
            onClick={handleRemoveClick}
            className={`text-xs px-2 py-1 rounded border transition-colors ${
              confirmRemove
                ? 'border-red-400 bg-red-50 text-red-700 hover:bg-red-100'
                : 'border-gray-300 text-gray-500 hover:border-red-300 hover:text-red-600 hover:bg-red-50'
            }`}
          >
            {confirmRemove ? 'Click again to confirm' : 'Remove'}
          </button>
        )}
      </div>

      {/* Step fields */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Step Name
            </label>
            <input
              type="text"
              value={step.name}
              onChange={(e) => handleField('name', e.target.value)}
              placeholder="e.g. Initial outreach"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Delay (days)
            </label>
            <input
              type="number"
              min="0"
              value={step.delayDays}
              onChange={(e) =>
                handleField('delayDays', parseInt(e.target.value, 10) || 0)
              }
              placeholder="0"
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-500">
              Days after the previous step (0 = immediate)
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Subject
          </label>
          <input
            type="text"
            value={step.subject}
            onChange={(e) => handleField('subject', e.target.value)}
            placeholder="e.g. Your listing on VinoSeeker"
            className={inputClass}
          />
        </div>

        <div>
          <div className="flex items-start justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Body
            </label>
          </div>
          <p className="text-xs text-gray-500 mb-2 font-mono leading-relaxed">
            Available variables:{' '}
            {vars.map((v, i) => (
              <span key={v}>
                <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                  {v}
                </code>
                {i < vars.length - 1 ? ' ' : ''}
              </span>
            ))}
          </p>
          <textarea
            rows={8}
            value={step.body}
            onChange={(e) => handleField('body', e.target.value)}
            placeholder="Enter the email body content. You can use HTML or template variables listed above."
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm font-mono text-sm"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

export default function OutreachJourneyNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isEditing = !!editId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [triggerFilter, setTriggerFilter] = useState({ ...EMPTY_TRIGGER_FILTER });
  const [steps, setSteps] = useState([EMPTY_STEP()]);

  // ── Load existing journey when editing ──────────────────────────────────────

  useEffect(() => {
    if (!isEditing) return;
    const load = async () => {
      try {
        setLoading(true);
        const result = await client.service('admin/outreach-journey').get(editId);
        setFormData({
          name: result.name || '',
          slug: result.slug || '',
          description: result.description || '',
          audience: result.audience || 'outreach',
          trigger: result.trigger || 'manual',
          masterTemplateId: result.masterTemplateId || '',
          fromEmail: result.fromEmail || '',
          fromName: result.fromName || '',
        });

        if (result.triggerFilter && result.triggerFilter.listingStatus) {
          setTriggerFilter({
            listingStatus: result.triggerFilter.listingStatus || [],
          });
        }

        if (result.steps && result.steps.length > 0) {
          setSteps(
            result.steps.map((s) => ({
              _key: Math.random().toString(36).slice(2),
              name: s.name || '',
              delayDays: s.delayDays ?? 0,
              subject: s.subject || '',
              body: s.body || '',
            }))
          );
        }

        // If the record already has a slug, treat it as manually edited
        if (result.slug) {
          setSlugManuallyEdited(true);
        }
      } catch (err) {
        console.error('Error loading journey:', err);
        toast.error('Failed to load journey');
        navigate('/admin/outreach/journeys');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [editId]);

  // ── Auto-generate slug from name ─────────────────────────────────────────────

  const setField = useCallback((field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'name' && !slugManuallyEdited) {
        next.slug = generateSlug(value);
      }
      return next;
    });
  }, [slugManuallyEdited]);

  const handleSlugChange = (value) => {
    setSlugManuallyEdited(true);
    setFormData((prev) => ({ ...prev, slug: value }));
  };

  // ── Trigger filter helpers ───────────────────────────────────────────────────

  const toggleListingStatus = (status) => {
    setTriggerFilter((prev) => {
      const current = prev.listingStatus || [];
      const next = current.includes(status)
        ? current.filter((s) => s !== status)
        : [...current, status];
      return { ...prev, listingStatus: next };
    });
  };

  const showTriggerFilter =
    formData.audience === 'outreach' || formData.audience === 'business';

  // ── Steps helpers ────────────────────────────────────────────────────────────

  const addStep = () => {
    setSteps((prev) => [...prev, EMPTY_STEP()]);
  };

  const updateStep = (index, updatedStep) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? updatedStep : s)));
  };

  const removeStep = (index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Build payload ────────────────────────────────────────────────────────────

  const buildPayload = () => {
    const payload = {
      name: formData.name.trim(),
      slug: formData.slug.trim(),
      audience: formData.audience,
      trigger: formData.trigger,
      steps: steps.map(({ _key, ...rest }) => rest),
    };

    if (formData.description.trim()) {
      payload.description = formData.description.trim();
    }
    if (formData.masterTemplateId.trim()) {
      payload.masterTemplateId = formData.masterTemplateId.trim();
    }
    if (formData.fromEmail.trim()) {
      payload.fromEmail = formData.fromEmail.trim();
    }
    if (formData.fromName.trim()) {
      payload.fromName = formData.fromName.trim();
    }

    if (showTriggerFilter && triggerFilter.listingStatus.length > 0) {
      payload.triggerFilter = { listingStatus: triggerFilter.listingStatus };
    }

    return payload;
  };

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Journey name is required');
      return;
    }
    if (!formData.slug.trim()) {
      toast.error('Slug is required');
      return;
    }
    if (steps.length === 0) {
      toast.error('At least one step is required');
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();

      let result;
      if (isEditing) {
        result = await client.service('admin/outreach-journey').patch(editId, payload);
        toast.success('Journey saved');
      } else {
        result = await client.service('admin/outreach-journey').create(payload);
        toast.success('Journey created');
      }
      navigate(`/admin/outreach/journeys/${result.id}`);
    } catch (err) {
      console.error('Error saving journey:', err);
      toast.error(err.message || 'Failed to save journey');
    } finally {
      setSaving(false);
    }
  };

  // ── Styles ───────────────────────────────────────────────────────────────────

  const inputClass =
    'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm';
  const checkboxClass =
    'h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary';

  // ── Loading state ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Page title="Edit Journey">
        <div className="py-12 text-center text-sm text-gray-500">
          Loading journey...
        </div>
      </Page>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Page title={isEditing ? 'Edit Journey' : 'New Journey'}>
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb
          elements={[
            { name: 'Outreach', href: '/admin/outreach/campaigns' },
            { name: 'Journeys', href: '/admin/outreach/journeys' },
            { name: isEditing ? 'Edit Journey' : 'New Journey' },
          ]}
        />
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">

        {/* ── Journey Details ──────────────────────────────────────────────── */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Journey Details
          </h3>
          <div className="space-y-4">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="e.g. New Business Onboarding"
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                  Slug <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="slug"
                  required
                  value={formData.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="e.g. new-business-onboarding"
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Auto-generated from name. Lowercase, hyphens only.
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <input
                type="text"
                id="description"
                value={formData.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Brief description of what this journey does"
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="audience" className="block text-sm font-medium text-gray-700">
                  Audience
                </label>
                <select
                  id="audience"
                  value={formData.audience}
                  onChange={(e) => setField('audience', e.target.value)}
                  className={inputClass}
                >
                  {AUDIENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="trigger" className="block text-sm font-medium text-gray-700">
                  Trigger
                </label>
                <select
                  id="trigger"
                  value={formData.trigger}
                  onChange={(e) => setField('trigger', e.target.value)}
                  className={inputClass}
                >
                  {TRIGGER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

          </div>
        </div>

        {/* ── Trigger Filter ───────────────────────────────────────────────── */}
        {showTriggerFilter && (
          <div className="bg-white shadow sm:rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              Trigger Filter
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Optionally restrict this journey to contacts associated with
              listings in specific statuses.
            </p>

            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-2">
                Listing Status
              </legend>
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {LISTING_STATUS_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-center">
                    <input
                      id={`ls-${opt.value}`}
                      type="checkbox"
                      checked={triggerFilter.listingStatus.includes(opt.value)}
                      onChange={() => toggleListingStatus(opt.value)}
                      className={checkboxClass}
                    />
                    <label
                      htmlFor={`ls-${opt.value}`}
                      className="ml-2 text-sm text-gray-700 capitalize"
                    >
                      {opt.label}
                    </label>
                  </div>
                ))}
              </div>
              {triggerFilter.listingStatus.length === 0 && (
                <p className="mt-2 text-xs text-gray-400">
                  No status filter applied — journey applies to all listing
                  statuses.
                </p>
              )}
            </fieldset>
          </div>
        )}

        {/* ── Email Defaults ───────────────────────────────────────────────── */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            Email Defaults
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Default sender and template settings applied to all steps unless
            overridden per step.
          </p>
          <div className="space-y-4">
            <div>
              <label htmlFor="masterTemplateId" className="block text-sm font-medium text-gray-700">
                Master Template ID
              </label>
              <input
                type="text"
                id="masterTemplateId"
                value={formData.masterTemplateId}
                onChange={(e) => setField('masterTemplateId', e.target.value)}
                placeholder="e.g. journey-base or 12345678"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">
                Postmark template alias or numeric ID used as the base layout
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="fromEmail" className="block text-sm font-medium text-gray-700">
                  From Email
                </label>
                <input
                  type="email"
                  id="fromEmail"
                  value={formData.fromEmail}
                  onChange={(e) => setField('fromEmail', e.target.value)}
                  placeholder="outreach@vinoseeker.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="fromName" className="block text-sm font-medium text-gray-700">
                  From Name
                </label>
                <input
                  type="text"
                  id="fromName"
                  value={formData.fromName}
                  onChange={(e) => setField('fromName', e.target.value)}
                  placeholder="VinoSeeker"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Steps Builder ────────────────────────────────────────────────── */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-medium text-gray-900">Steps</h3>
            <span className="text-sm text-gray-500">
              {steps.length} step{steps.length !== 1 ? 's' : ''}
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-5">
            Each step is one email sent at the configured delay. Steps are
            executed in order.
          </p>

          {steps.map((step, index) => (
            <StepCard
              key={step._key}
              step={step}
              index={index}
              audience={formData.audience}
              totalSteps={steps.length}
              onChange={(updated) => updateStep(index, updated)}
              onRemove={() => removeStep(index)}
            />
          ))}

          <button
            type="button"
            onClick={addStep}
            className="mt-2 inline-flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-800 hover:bg-gray-50 transition-colors"
          >
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Add Step
          </button>
        </div>

        {/* ── Actions ──────────────────────────────────────────────────────── */}
        <div className="flex justify-end items-center gap-3 pb-8">
          <button
            type="button"
            onClick={() => navigate('/admin/outreach/journeys')}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <PrimaryButton type="submit" disabled={saving}>
            {saving
              ? isEditing
                ? 'Saving...'
                : 'Creating...'
              : isEditing
              ? 'Save Journey'
              : 'Create Journey'}
          </PrimaryButton>
        </div>

      </form>
    </Page>
  );
}
